import { useState, useEffect, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { MAP_DATA_QUERY_KEY } from '@/hooks/useMapData';
import { GEO_INTEL_QUERY_KEY } from '@/hooks/useGeoIntelData';

export interface Cluster {
  id: string;
  name: string;
  color: string;
  sede_municipio_ibge: string | null;
  created_at: string;
  updated_at: string;
}

export function useClusters() {
  const [clusters, setClusters] = useState<Cluster[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const fetchClusters = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('clusters')
        .select('*')
        .order('name');

      if (error) throw error;
      setClusters(data || []);
    } catch (error: any) {
      toast({
        variant: 'destructive',
        title: 'Erro ao buscar clusters',
        description: error.message,
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchClusters();
  }, [fetchClusters]);

  const createCluster = useCallback(async (
    cluster: Omit<Cluster, 'id' | 'created_at' | 'updated_at'>
  ) => {
    try {
      const { data, error } = await supabase
        .from('clusters')
        .insert(cluster)
        .select()
        .single();

      if (error) throw error;

      toast({
        title: 'Cluster criado',
        description: `${data.name} foi criado com sucesso.`,
      });

      await fetchClusters();
      await queryClient.invalidateQueries({ queryKey: MAP_DATA_QUERY_KEY });
      await queryClient.invalidateQueries({ queryKey: GEO_INTEL_QUERY_KEY });
      await queryClient.invalidateQueries({ queryKey: ['emp-feature-flags'] });

      return data;
    } catch (error: any) {
      toast({
        variant: 'destructive',
        title: 'Erro ao criar cluster',
        description: error.message,
      });
      return null;
    }
  }, [toast, fetchClusters, queryClient]);

  const updateCluster = useCallback(async (
    id: string,
    updates: Partial<Cluster>
  ) => {
    try {
      const { data, error } = await supabase
        .from('clusters')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      toast({
        title: 'Cluster atualizado',
        description: `${data.name} foi atualizado com sucesso.`,
      });

      await fetchClusters();
      await queryClient.invalidateQueries({ queryKey: MAP_DATA_QUERY_KEY });
      await queryClient.invalidateQueries({ queryKey: GEO_INTEL_QUERY_KEY });

      return data;
    } catch (error: any) {
      toast({
        variant: 'destructive',
        title: 'Erro ao atualizar cluster',
        description: error.message,
      });
      return null;
    }
  }, [toast, fetchClusters, queryClient]);

  const deleteCluster = useCallback(async (id: string) => {
    try {
      const { error } = await supabase
        .from('clusters')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast({
        title: 'Cluster excluído',
        description: 'Cluster foi excluído com sucesso.',
      });

      fetchClusters();
      queryClient.invalidateQueries({ queryKey: MAP_DATA_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: ['emp-feature-flags'] });
      return true;
    } catch (error: any) {
      toast({
        variant: 'destructive',
        title: 'Erro ao excluir cluster',
        description: error.message,
      });
      return false;
    }
  }, [toast, fetchClusters, queryClient]);

  return {
    clusters,
    loading,
    refetch: fetchClusters,
    createCluster,
    updateCluster,
    deleteCluster,
  };
}
